import { useEffect, useState } from 'react';
import { useAccessToken } from '@robinpowered/dashboard-apps-common';
import { config } from 'config';
import {
  useGetOrganizationSlugByIdQuery,
  useGetOrganizationsForSwitcherQuery,
  GetOrganizationSlugByIdQuery,
} from 'generated/graphql';

/**
 * Result of `useResolveOrgFromEvent`.
 *
 * - `loading`: a network call is still in flight (event probe or org slug lookup).
 * - `orgSlug`: resolved slug, set once both calls succeed.
 * - `locationId`: the event's `space.location.id`, exposed so the caller can
 *   navigate the meeting-rooms grid to the event's office.
 * - `eventStartDateTime` / `eventTimeZone`: the event's start instant and its
 *   own time zone string, exposed so the caller can format the right
 *   calendar day in the office's timezone for the meeting-rooms grid.
 * - `error`: set if either call fails irrecoverably; `orgSlug` is null in that case.
 */
export type UseResolveOrgFromEventResult = {
  orgSlug: string | null;
  locationId: number | null;
  eventStartDateTime: string | null;
  eventTimeZone: string | null;
  loading: boolean;
  error: Error | null;
};

type EventProbeResponse = {
  data?: {
    start?: {
      date_time?: string | null;
      time_zone?: string | null;
    } | null;
    space?: {
      location?: {
        id?: number | string | null;
        account_id?: number | string | null;
      } | null;
    } | null;
  };
};

type ErrorBody = {
  meta?: {
    message?: string;
    errors?: { reason?: string }[];
  };
};

const isMissingTenantError = (status: number, body: unknown): boolean => {
  if (status !== 400) return false;
  const meta = (body as ErrorBody | undefined)?.meta;
  if (!meta) return false;
  if (
    Array.isArray(meta.errors) &&
    meta.errors.some((e) => e?.reason === 'missing_tenant_id')
  ) {
    return true;
  }
  if (
    typeof meta.message === 'string' &&
    meta.message.includes("Couldn't infer `organization` parameter")
  ) {
    return true;
  }
  return false;
};

/**
 * Resolve the org slug for a bare event URL like `/event/:eventID` where no
 * org context is present. Mirrors the legacy AngularJS resolver's strategy
 * (see angular/app/scripts/appConfig.js:1726-1793 in the legacy repo):
 *
 *   1. Issue a tenant-less `GET ${apiUrl}/events/:eventID`.
 *   2. On 200 → read `space.location.account_id` and look up the slug.
 *   3. On 400 with `missing_tenant_id` (proxied events without a self-evident
 *      tenant) → fall back to the user's first org from the GraphQL
 *      organizations list, re-issue the REST call with that org's id as the
 *      `tenant-id` header, then take `account_id` from the response (which
 *      may differ from the fallback org for proxied events).
 *   4. Slug lookup is run against Apollo with an explicit `tenant-id` header
 *      set to the resolved account id, since the shell's normal `tenantId`
 *      context isn't populated on a bare `/event/:id` URL.
 */
export const useResolveOrgFromEvent = (
  eventID: string | undefined
): UseResolveOrgFromEventResult => {
  const { accessToken } = useAccessToken(config.sessionCookieName);

  const [accountId, setAccountId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [eventStartDateTime, setEventStartDateTime] = useState<string | null>(
    null
  );
  const [eventTimeZone, setEventTimeZone] = useState<string | null>(null);
  const [probeLoading, setProbeLoading] = useState<boolean>(true);
  const [probeError, setProbeError] = useState<Error | null>(null);

  // Pull the user's first org as the fallback tenant context. We always fetch
  // this — `skip` would mean we can't recover from a `missing_tenant_id` in
  // time. The query is small (first: 1).
  const { data: switcherData, error: switcherError } =
    useGetOrganizationsForSwitcherQuery({
      variables: { first: 1 },
      skip: !accessToken,
    });
  const fallbackTenantId =
    switcherData?.getCurrentUserAndOrgs?.organizationsForSwitcher
      ?.organizations[0]?.id ?? null;

  useEffect(() => {
    if (!eventID || !accessToken) {
      setProbeLoading(!!eventID);
      return;
    }

    const controller = new AbortController();
    setProbeLoading(true);
    setProbeError(null);
    setAccountId(null);
    setLocationId(null);
    setEventStartDateTime(null);
    setEventTimeZone(null);

    const probe = async (tenantId: string | null) => {
      // Without `include=space.location`, the bare event endpoint returns
      // only top-level event fields (no nested space/location). We need
      // `space.location.account_id` to derive the org, so request that
      // association explicitly. The legacy AngularJS resolver requested a
      // much larger include set; we only need the minimum here.
      const url = `${config.apiUrl}/events/${eventID}?include=space.location`;
      const headers: Record<string, string> = {
        Authorization: `Access-Token ${accessToken}`,
        'Content-Type': 'application/json',
      };
      if (tenantId) headers['tenant-id'] = tenantId;
      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        headers,
      });
      const text = await response.text();
      let body: unknown = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch {
        // ignore — body parsing only used for missing-tenant detection
      }
      return { response, body };
    };

    (async () => {
      try {
        let { response, body } = await probe(null);

        if (!response.ok && isMissingTenantError(response.status, body)) {
          // Need a tenant context to look this event up. Use the user's first
          // org as a best guess — this matches the legacy resolver.
          if (!fallbackTenantId) {
            throw new Error(
              `Event probe requires tenant context but no organizations ` +
                `are available for the current user (event id: ${eventID})`
            );
          }
          ({ response, body } = await probe(fallbackTenantId));
        }

        if (!response.ok) {
          throw new Error(
            `Event probe failed: ${response.status} ${response.statusText}` +
              (body
                ? ` — ${JSON.stringify((body as ErrorBody)?.meta ?? body)}`
                : '')
          );
        }

        const typedBody = body as EventProbeResponse | null;
        // Capture the event's calendar location + start instant whenever
        // the probe returns them, regardless of which org-resolution branch
        // we take below. The caller uses these to seed `currentDate` and
        // `locationId` on the meeting-rooms grid so the redirect lands on
        // the event's day and office. Missing fields stay null and the
        // caller silently omits them from the redirect URL.
        const rawLocationId = typedBody?.data?.space?.location?.id;
        if (rawLocationId !== undefined && rawLocationId !== null) {
          const parsedLocationId = Number(rawLocationId);
          if (Number.isFinite(parsedLocationId)) {
            setLocationId(parsedLocationId);
          }
        }
        const rawStart = typedBody?.data?.start?.date_time;
        if (rawStart) setEventStartDateTime(rawStart);
        const rawTz = typedBody?.data?.start?.time_zone;
        if (rawTz) setEventTimeZone(rawTz);

        const rawAccountId = typedBody?.data?.space?.location?.account_id;
        if (rawAccountId !== undefined && rawAccountId !== null) {
          setAccountId(String(rawAccountId));
          return;
        }

        // The event probe succeeded but the response doesn't carry a
        // space association we can derive an org from — common for
        // calendar-synced events that have no Robin space attached
        // (e.g. an external Outlook/Google event whose id is the
        // provider's long external id). Fall back to the user's first
        // org (the same tenant the retry path would have used). If the
        // probe needed `fallbackTenantId` to succeed at all, this is
        // also the tenant the successful response came from.
        if (fallbackTenantId) {
          setAccountId(fallbackTenantId);
          return;
        }
        // eslint-disable-next-line no-console
        console.error(
          '[useResolveOrgFromEvent] event response shape:',
          typedBody
        );
        throw new Error(
          `Event probe response is missing space.location.account_id ` +
            `and no fallback tenant is available (event id: ${eventID})`
        );
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        const error = err instanceof Error ? err : new Error(String(err));
        // eslint-disable-next-line no-console
        console.error('[useResolveOrgFromEvent] probe error:', error);
        setProbeError(error);
      } finally {
        if (!controller.signal.aborted) setProbeLoading(false);
      }
    })();

    return () => controller.abort();
  }, [eventID, accessToken, fallbackTenantId]);

  // Set the tenant-id header explicitly on the slug-lookup query so the
  // federation gateway can resolve it. `AuthContext.tenantId` isn't populated
  // for bare `/event/:id` URLs.
  const {
    data: slugData,
    loading: slugLoading,
    error: slugError,
  } = useGetOrganizationSlugByIdQuery({
    variables: { id: accountId ?? '' },
    skip: !accountId,
    context: accountId ? { headers: { 'tenant-id': accountId } } : undefined,
  });

  const orgSlug = pickSlug(slugData);
  const loading = probeLoading || (!!accountId && slugLoading);

  let error: Error | null = null;
  if (probeError) {
    error = probeError;
  } else if (switcherError) {
    error = new Error(
      `Failed to load fallback organization list: ${switcherError.message}`
    );
  } else if (slugError) {
    error = new Error(
      `Failed to look up org slug for account ${accountId}: ${slugError.message}`
    );
  } else if (!loading && accountId && !orgSlug) {
    error = new Error(
      `Organization with id ${accountId} not found (no slug returned)`
    );
  }

  if (error) {
    // eslint-disable-next-line no-console
    console.error('[useResolveOrgFromEvent] resolve error:', error);
  }

  return {
    orgSlug: error ? null : orgSlug,
    locationId: error ? null : locationId,
    eventStartDateTime: error ? null : eventStartDateTime,
    eventTimeZone: error ? null : eventTimeZone,
    loading,
    error,
  };
};

const pickSlug = (
  data: GetOrganizationSlugByIdQuery | undefined
): string | null => {
  const slug = data?.getOrganizationById?.slug;
  return slug ?? null;
};
